import {
  init,
  browserTracingIntegration,
  replayIntegration,
  consoleLoggingIntegration,
} from "@sentry/remix";
import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";

// Initialize Sentry in production only. No dev/local opt-in — we never want
// Sentry events originating from `npm run dev`.
const sentryDsn =
  import.meta.env.PUBLIC_SENTRY_DSN || process.env.PUBLIC_SENTRY_DSN;
const shouldInitSentry =
  Boolean(sentryDsn) &&
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

if (shouldInitSentry) {
  init({
    dsn: sentryDsn,
    // Must match the release used when uploading sourcemaps in vite.config.ts
    // and the one used in instrumentation.server.mjs.
    release: process.env.SENTRY_RELEASE,
    environment: "production",

    tracesSampleRate: 0.1,

    // Session Replay is expensive on the main thread during hydrate.
    // Keep error replays only so listing/area pages are not blocked by
    // continuous session recording (GSC-safe: no UI change).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    enableLogs: true,
    sendDefaultPii: true,

    integrations: [
      browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      replayIntegration({
        // Privacy defaults - flip to false only if you intentionally want
        // to capture text/media from your replays.
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Forward console.error / console.warn / console.info to Sentry Logs.
      // Requires `enableLogs: true` (above) AND the "Logs" feature enabled
      // in Sentry project settings.
      consoleLoggingIntegration({
        levels: ["error", "warn", "info"],
      }),
    ],
  });
}

// Vite's modulepreload helper throws when a lazy chunk's CSS/JS fails to load
// (Google's URL Inspection renderer often drops mapbox-gl-*.css). preventDefault()
// stops that throw from taking down the tree — BUT Vite then fulfills the
// dynamic import with `undefined`. React.lazy reads `undefined.default` and
// throws TypeError, which is what GSC showed on geosearch-desktop.
// Lazy imports that can fail under a crawler MUST use `loadLazyModule`.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
  });
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>
  );

  // Initialize BotID AFTER hydration
  // CRITICAL: Only protect routes where you're using checkBotId() on the server
  // initBotId({
  //   protect: [
  //     { path: "/*/listings/*", method: "GET" },
  //     { path: "/*/workspaces/*", method: "GET" },
  //     { path: "/*/workspaces/*/*", method: "GET" },
  //     { path: "/*/properties/*", method: "GET" },
  //     { path: "/*/blog/*", method: "GET" },
  //     { path: "/*/areas/*", method: "GET" },
  //     { path: "/*/buildings/*", method: "GET" },
  //     { path: "/*/testimonials/*", method: "GET" },
  //     { path: "/*/compare/*", method: "GET" },
  //     { path: "/*/favorites/*", method: "GET" },
  //     { path: "/*/search", method: "GET" },
  //     { path: "/api/*", method: "POST" },
  //   ],
  // });
});
